export type PublicCacheResource =
  | 'product-contents-all'
  | 'product-contents-top'
  | 'news'
  | 'news-images';

/**
 * 全ユーザー共通の公開データを、Nitro のキャッシュ（`server/api/public-cache/[resource].get.ts`・30秒）から取る。
 *
 * - `useApiFetch` は baseURL を API に固定しているので使わず、相対パスで呼ぶ（SSR ではネットワークを通らない内部呼び出しになる）
 * - SSR とハイドレーション中は `key` を固定にして、ハイドレーション時に SSR の payload を再利用する。
 *   **ハイドレーション後のクライアントでは、呼び出しごとに別の key にする**（`useApiFetch` と同じ）。
 *   `getNewsImages` はルート遷移のたびに呼ばれ、/cart では2か所から同時に呼ばれる。key を共有すると、
 *   `dedupe: 'defer'` では止まった1本を以後の呼び出しが全部待ち、`'cancel'` では応答と取り直しが重なったときに
 *   先の呼び出しが空の結果を受け取る（nuxt 3.21.11 `dist/app/composables/asyncData.js:346-349`・`:389`）
 * - `retry: 0`。Nitro ルートの 502 をリトライすると、障害中の API 取得が倍になる
 * - タイムアウトは既定で付けない。SSR では内側（10秒）が先に効く。外側が先に切れると本文の無いエラーになり、空データの 200 に戻る
 */
export const usePublicCache = async <T>(
  resource: PublicCacheResource,
  options: { timeout?: number } = {}
) => {
  const nuxtApp = useNuxtApp();
  const sharedKey: string = `public-cache:${resource}`;
  const key: string =
    import.meta.client && !nuxtApp.isHydrating ? `${sharedKey}:${crypto.randomUUID()}` : sharedKey;
  /*
   * SSR の payload から引き継いだ失敗か。`isHydrating` は CSR のルートでも初期値が true なのでそれだけでは判定せず、
   * `_errors` はハイドレーション後のクライアントでの失敗でも埋まるので、それだけでも判定しない
   */
  const hasSsrError: boolean =
    import.meta.client && nuxtApp.isHydrating && !!nuxtApp.payload._errors[key];
  const result = await useFetch<T>(`/api/public-cache/${resource}`, {
    key,
    retry: 0,
    ...options,
  });

  /*
   * **SSR で失敗した取得は、ハイドレーション時に1回だけ取り直す。** payload にエラーがあると、
   * `useFetch` は取り直さずにそのエラーを返す（nuxt 3.21.11 `dist/app/composables/asyncData.js:114-118`）。
   * そのままだとクライアントの plugin 201・202 が例外で止まり、後続の plugin（端末判定・yup・dayjs）も
   * 動かない（`dist/app/entry.js:60-62`）。エラーページから NuxtLink で移動するとエラーは消えるが、
   * plugin は再実行されないので、再読み込みまで商品・お知らせが空のまま使われ続ける。
   * 取り直しも失敗したときは、変更前（クライアントで取得して失敗したとき）と同じく再読み込みまで空のまま。
   */
  if (hasSsrError && result.error.value) {
    await result.refresh();
  }

  return result;
};
